import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { AdminHeader, Card, Btn, Input, Toggle, Empty } from "@/components/admin/ui";
import { resolveMediaUrl, handleMediaError } from "@/lib/media";
import { toast } from "sonner";
import { Upload, Image as ImageIcon, Undo2 } from "lucide-react";

export default function AdminHomepage() {
  const [sections, setSections] = useState([]);
  const [hero, setHero] = useState(null);
  const [pending, setPending] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [heroError, setHeroError] = useState(null);
  const fileRef = useRef(null);

  const loadSections = () => api.get("/admin/homepage").then(({ data }) => setSections(data.sections));
  const loadHero = () => api.get("/admin/homepage-hero").then(({ data }) => setHero(data));
  useEffect(() => { loadSections(); loadHero(); }, []);

  const save = async (s) => {
    await api.put(`/admin/homepage/${s.id}`, { title: s.title, subtitle: s.subtitle, order: s.order, active: s.active });
    toast.success("Section updated");
  };
  const update = (id, k, v) => setSections((prev) => prev.map((s) => (s.id === id ? { ...s, [k]: v } : s)));

  const chooseFile = async (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png"].includes(file.type)) { toast.error("Only JPG/PNG images are allowed"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Image must be 10 MB or smaller"); return; }
    setUploading(true); setHeroError(null); setPending(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/uploads/hero-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPending({ url: data.url, key: data.key });
      toast.success("Image uploaded — press Publish to make it live");
    } catch (e) {
      const msg = apiError(e);
      setHeroError(msg);
      toast.error(msg);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const publish = async () => {
    if (!pending?.key) return;
    setPublishing(true); setHeroError(null);
    try {
      const { data } = await api.put("/admin/homepage-hero", { key: pending.key });
      setHero((h) => ({ ...(h || {}), active: data.active, previous: data.previous }));
      setPending(null);
      toast.success("Hero image published");
    } catch (e) {
      const msg = apiError(e);
      setHeroError(msg);
      toast.error(msg);
    } finally { setPublishing(false); }
  };

  const restore = async () => {
    setRestoring(true); setHeroError(null);
    try {
      const { data } = await api.post("/admin/homepage-hero/restore");
      setHero((h) => ({ ...(h || {}), active: data.active, previous: data.previous }));
      toast.success("Previous hero image restored");
    } catch (e) {
      const msg = apiError(e);
      setHeroError(msg);
      toast.error(msg);
    } finally { setRestoring(false); }
  };

  const activeImage = hero?.active?.url || null;
  const previewImage = pending?.url || activeImage;

  return (
    <div>
      <AdminHeader title="Homepage" subtitle="Hero background & merchandising rows" />

      <div className="space-y-6">
        <Card className="p-5" data-testid="homepage-hero-card">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-semibold text-slate-900">Hero Background</h3>
            {hero && !hero.storageConfigured && (
              <span className="text-xs font-semibold text-amber-600" data-testid="hero-storage-warning">Image storage not configured</span>
            )}
          </div>
          <p className="text-xs text-slate-500 mb-4">
            The image shown behind the fixed hero video on the storefront. Upload a JPG or PNG
            (min 1600×600px, up to 10 MB), preview, then publish. The hero video itself is a fixed
            build asset and cannot be changed here.
          </p>

          <div className="flex flex-col sm:flex-row gap-4">
            <div className="shrink-0">
              {previewImage ? (
                <img src={resolveMediaUrl(previewImage)} alt="Hero background preview" onError={handleMediaError}
                  data-testid="hero-image-preview"
                  className="h-32 w-full sm:w-56 rounded-lg bg-slate-50 object-cover border border-slate-200" />
              ) : (
                <div className="h-32 w-full sm:w-56 rounded-lg bg-slate-50 border border-dashed border-slate-300 grid place-items-center text-slate-400"
                  data-testid="hero-image-placeholder"><ImageIcon className="h-6 w-6" /></div>
              )}
            </div>

            <div className="flex-1 space-y-3">
              <input ref={fileRef} type="file" accept="image/jpeg,image/png" className="hidden"
                onChange={(e) => chooseFile(e.target.files?.[0])} data-testid="hero-image-file" />
              <div className="flex flex-wrap items-center gap-2">
                <Btn variant="outline" onClick={() => fileRef.current?.click()} loading={uploading} data-testid="hero-image-upload-btn">
                  <Upload className="h-4 w-4" /> {activeImage ? "Upload new image" : "Upload image"}
                </Btn>
                {pending && (
                  <>
                    <Btn variant="accent" onClick={publish} loading={publishing} data-testid="hero-image-publish-btn">Publish</Btn>
                    <button type="button" onClick={() => setPending(null)} className="text-xs text-slate-500 hover:underline"
                      data-testid="hero-image-cancel-btn">Cancel</button>
                  </>
                )}
                {hero?.previous && (
                  <Btn variant="outline" onClick={restore} loading={restoring} data-testid="hero-image-restore-btn">
                    <Undo2 className="h-4 w-4" /> Restore previous
                  </Btn>
                )}
              </div>

              {pending && (
                <p className="text-xs text-amber-600" data-testid="hero-image-pending-note">
                  New image uploaded — preview above. Press Publish to make it live.
                </p>
              )}
              {heroError && <p className="text-xs text-red-600" data-testid="hero-image-error">{heroError}</p>}

              <div className="text-xs text-slate-500 space-y-1">
                {activeImage && (
                  <p data-testid="hero-image-current">Current image: <span className="font-mono text-slate-600 break-all">{activeImage}</span></p>
                )}
                {hero?.active?.updatedAt && <p>Last updated: {new Date(hero.active.updatedAt).toLocaleString()}</p>}
              </div>
            </div>
          </div>
        </Card>

        <div className="space-y-4">
          {sections.map((s) => (
            <Card key={s.id} className="p-5" data-testid={`homepage-${s.key}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold uppercase tracking-wider text-amber-600">{s.key}</span>
                <Toggle label="Active" checked={s.active} onChange={(v) => update(s.id, "active", v)} />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Input label="Title" value={s.title} onChange={(e) => update(s.id, "title", e.target.value)} />
                <Input label="Subtitle" value={s.subtitle} onChange={(e) => update(s.id, "subtitle", e.target.value)} />
              </div>
              <div className="flex items-center gap-3 mt-3">
                <Input label="Order" type="number" value={s.order} onChange={(e) => update(s.id, "order", Number(e.target.value))} className="w-24" />
                <Btn variant="accent" className="mt-5" onClick={() => save(s)} data-testid={`save-homepage-${s.key}`}>Save</Btn>
              </div>
            </Card>
          ))}
          {sections.length === 0 && <Empty />}
        </div>
      </div>
    </div>
  );
}
